<script lang="ts">
  import MetaData from "$lib/MetaData.svelte";
  import type { BorettslagContract } from "$lib/types/contracts";
  import Lead from "$lib/Lead.svelte";
  import LeadsBanner from "$lib/LeadsBannerFrame.svelte";
  import BoligselskapLeadsForm from "./BoligselskapLeadsForm.svelte";
  import TeaserSection from "$lib/TeaserSection.svelte";
  import type { Teaser } from "$lib/types/shared";
  import lang from "$lib/langStore";
  import SanityPicture from "$lib/SanityPicture.svelte";
  import Cover from "$lib/Cover.svelte";
  import { breakpoints as br } from "$lib/utils/getBreakpoints";
  import { contentBlockComponents } from "$lib/portableText/utils";
  import { PortableText } from "@portabletext/svelte";
  import Picture from "$lib/Picture.svelte";
  import PortableTextContent from "$lib/portableText/PortableTextContent.svelte";

  interface Props {
    data: {
      contract: Readonly<BorettslagContract>;
      app_teaser: Readonly<Teaser>;
    };
  }

  let { data }: Props = $props();

  let borettslag = $derived(data?.contract);
  let contract = $derived(data?.contract?.contract);
  let heroString = $derived(data?.contract?.hero);
  let app_teaser = $derived(data?.app_teaser);
</script>

<MetaData
  data={{
    title: contract.name,
    description: contract.description,
  }}
/>

<Cover
  class="mt-2 mb-8"
  --cover-img-lqip={`url("${contract.picture?.lqip}")`}
  pictureFallbackBackground
>
  <div
    class="pt-[calc(5*var(--text-h-hero))] pb-8 laptop:pr-[calc(12.5vw-var(--w-cover-x))] text-white"
  >
    <h1 class="my-0 text-h-hero font-title tablet:leading-snug">
      {heroString}
    </h1>
  </div>

  {#snippet background()}
    <div>
      {#if contract.picture?.src}
        <SanityPicture
          pictureObject={contract.picture}
          sizes={`(min-width: ${br.desktop}) 85vw, (min-width: ${br.laptop}) 90vw, 100vw`}
          class="h-full w-full object-cover laptop:rounded-2xl"
          loading="eager"
        />
      {/if}
    </div>
  {/snippet}
</Cover>

<div class="mx-edge-x">
  <div class="children-max-w-prose [--space-block:var(--s-6)] laptop:ml-cover-x">
    <h2 class="mb-0">{contract.name}</h2>
    <Lead class="max-w-prose">{contract.description}</Lead>

    <div class="text-body">
      <PortableText
        value={contract.content}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
    </div>
  </div>
</div>

<LeadsBanner
  title="Bli kontaktet"
  subTitle="Vi ringer deg gjerne!"
  isBorettslag={true}
  emailLabel="Du kan også sende oss en henvendelse på"
  customEmail="boligselskap@ishavskraft.no"
  class="laptop:px-cover-x laptop:py-8 my-12"
>
  <BoligselskapLeadsForm />
</LeadsBanner>

<div class="mx-edge-x text-body">
  <div class="mt-16 children-my-1 text-base text-[--fg-muted] font-bold italic max-w-prose">
    <PortableText
      value={contract.finePrint}
      components={contentBlockComponents}
      onMissingComponent={false}
    />
  </div>
  <div class="responsive-wrapper frame">
    {#if borettslag.bospot_img?.src}
      <Picture
        src={borettslag.bospot_img.src}
        alt={borettslag.bospot_img.alt}
        srcset={borettslag.bospot_img.srcset}
        srcsetWebp={borettslag.bospot_img.srcsetWebp}
        class="object-cover w-full tablet:w-35% tablet:h-initial"
        --picture-aspect-ratio="16/9"
      />
    {/if}
    <div class="px-4 my-6 flex-1">
      <h2>{borettslag.bospot_title ?? "Bospot"}</h2>
      <PortableText
        value={borettslag.bospot}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
      <!-- <TypeformButton
        class="button-primary-success"
        url="https://form.typeform.com/to/euUF2JJe"
        customParameters={{ produkt: "BoSpot", product_id: contract.product_id || "" }}
      >
        Bestill BoSpot
      </TypeformButton> -->
    </div>
  </div>

  <div class="responsive-wrapper frame flex-col-reverse">
    {#if borettslag.varmepumpe_img?.src}
      <Picture
        src={borettslag.varmepumpe_img.src}
        alt={borettslag.varmepumpe_img.alt}
        srcset={borettslag.varmepumpe_img.srcset}
        srcsetWebp={borettslag.varmepumpe_img.srcsetWebp}
        class="object-cover w-full tablet:w-35% tablet:h-initial"
        --picture-aspect-ratio="16/9"
      />
    {/if}
    <div class="px-4 my-6 flex-1">
      <h2>{borettslag.varmepumpe_title ?? "Varmepumpe"}</h2>
      <PortableText
        value={borettslag.varmepumpe}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
    </div>
  </div>

  <div class="responsive-wrapper frame">
    {#if borettslag.elbil_lading_img?.src}
      <Picture
        src={borettslag.elbil_lading_img.src}
        alt={borettslag.elbil_lading_img.alt}
        srcset={borettslag.elbil_lading_img.srcset}
        srcsetWebp={borettslag.elbil_lading_img.srcsetWebp}
        class="object-cover w-full tablet:w-35% tablet:h-initial"
        --picture-aspect-ratio="16/9"
      />
    {/if}
    <div class="px-4 my-6 flex-1">
      <h2>{borettslag.elbil_lading_title ?? "Elbil lading"}</h2>
      <PortableText
        value={borettslag.elbil_lading}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
    </div>
  </div>
  {#if borettslag.zaptec_title}
    <div class="responsive-wrapper blue-box flex-col-reverse">
      {#if borettslag.zaptec_img?.src}
        <Picture
          src={borettslag.zaptec_img.src}
          alt={borettslag.zaptec_img.alt}
          srcset={borettslag.zaptec_img.srcset}
          srcsetWebp={borettslag.zaptec_img.srcsetWebp}
          class="object-cover w-full tablet:w-35% tablet:h-initial"
          --picture-aspect-ratio="16/9"
        />
      {/if}
      <div class="px-4 my-6 flex-1">
        <h2>{borettslag.zaptec_title ?? "Zaptec"}</h2>
        <PortableText
          value={borettslag.zaptec}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
    </div>
  {/if}
</div>

<hr class="mx-edge-x my-8 text-[--fg-muted]" />

{#if app_teaser}
  <TeaserSection
    class="my-28"
    circleImage={false}
    teaser={app_teaser}
    href="/app"
    lang={$lang ? "nb" : undefined}
  />
{/if}

{#if contract?.additional_content}
  <div>
    <hr class="mx-edge-x mt-12 mb-16" />

    <PortableTextContent --font-size="var(--text-sm)" content={contract.additional_content} />
  </div>
{/if}

<style lang="postcss">
  .frame {
    background-color: var(--skodde);
    border-radius: 1rem;
    margin-block: var(--s-12);
  }

  .frame :global(img) {
    border-radius: 1rem 1rem 0 0;
  }

  .responsive-wrapper {
    display: flex;
    flex-direction: column;
  }

  @media (--tablet) {
    .frame {
      padding-inline-end: 2vw;
    }

    .frame :global(img) {
      border-radius: 1rem 0 0 1rem;
    }

    .frame:nth-child(even) {
      padding-inline-end: 0;
    }

    .frame:nth-child(even) :global(img) {
      border-radius: 0 1rem 1rem 0;
    }

    .responsive-wrapper {
      flex-direction: row;
      gap: 2rem;
    }

    .responsive-wrapper:nth-child(even) {
      flex-direction: row-reverse;
    }
  }

  @media (--laptop) {
    .frame {
      padding-inline-end: var(--w-cover-x);
    }

    .frame:nth-child(even) {
      padding-inline: var(--w-cover-x) 0;
    }
  }
</style>
